import { atom, useSetAtom } from 'jotai'
import { useEffect } from 'react'

import type { HospitalityPermissions } from '../types/rooms'

/**
 * Atoms for shared reference data across hotels-ui components.
 * These eliminate prop drilling for stable context data.
 *
 * Set these atoms in the consuming app (e.g., admin3) where PBX context
 * is available. Use `useHotelsDataAtoms` hook to hydrate atoms from props.
 * Components read from atoms using useAtomValue() instead of receiving props.
 */

/** Numeric PBX ID — used for rooms, call history, reset endpoints */
export const pbxIdAtom = atom<number>(0)

/** PBX UUID string — used for wake-up call endpoints */
export const pbxUuidAtom = atom<string>('')

/** Hospitality role-based permissions for the current user */
export const permissionsAtom = atom<HospitalityPermissions>({})

/**
 * Hook to hydrate hotels data atoms from props/context.
 * Call this once in the consuming app's page wrapper after
 * extracting PBX context from route params.
 *
 * @example
 * ```tsx
 * // In admin3 HotelsDashboardPage wrapper
 * useHotelsDataAtoms({
 *   pbxId: Number(params.pbxId),
 *   pbxUuid: pbxData.uuid,
 *   permissions: userPermissions,
 * })
 * ```
 */
export const useHotelsDataAtoms = (data: {
  pbxId?: number
  pbxUuid?: string
  permissions?: HospitalityPermissions
}) => {
  const setPbxId = useSetAtom(pbxIdAtom)
  const setPbxUuid = useSetAtom(pbxUuidAtom)
  const setPermissions = useSetAtom(permissionsAtom)

  useEffect(() => {
    if (data.pbxId !== undefined) {
      setPbxId(data.pbxId)
    }
  }, [data.pbxId, setPbxId])

  useEffect(() => {
    if (data.pbxUuid !== undefined) {
      setPbxUuid(data.pbxUuid)
    }
  }, [data.pbxUuid, setPbxUuid])

  useEffect(() => {
    if (data.permissions !== undefined) {
      setPermissions(data.permissions)
    }
  }, [data.permissions, setPermissions])
}
